import { Component } from '@theme/component'
import { loadGsapCore } from '@theme/gsap-runtime'

const Transition = {
  inDurationS: 0.45,
  outDurationS: 0.3,
  staggerS: 0.025
}

class AboutPanel extends Component {
  requiredRefs = ['tablist', 'panel', 'display']

  connectedCallback() {
    super.connectedCallback()
    this.handleTabClick = this.handleTabClick.bind(this)
    this.handleKeydown = this.handleKeydown.bind(this)
    this.reducedMotionQuery = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    )
    this.activeTimeline = null
    this.swapRequestId = 0

    for (const tab of this.getTabs()) {
      tab.addEventListener('click', this.handleTabClick)
    }
    this.refs.tablist.addEventListener('keydown', this.handleKeydown)

    this.renderHeightGhosts()
    this.wrapDisplayInWords()
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.swapRequestId += 1
    for (const tab of this.getTabs()) {
      tab.removeEventListener('click', this.handleTabClick)
    }
    this.refs.tablist?.removeEventListener('keydown', this.handleKeydown)
    this.activeTimeline?.kill()
  }

  getTabs() {
    return this.refs.tab ?? []
  }

  handleTabClick(event) {
    const tab = event.currentTarget
    const text = tab.dataset.aboutText ?? ''
    this.activate(tab, text)
  }

  handleKeydown(event) {
    const tabs = this.getTabs()
    if (tabs.length === 0) return

    const currentIndex = this.getFocusedTabIndex(tabs)
    if (currentIndex === -1) return

    const nextIndex = this.getNextTabIndex(event.key, currentIndex, tabs.length)
    if (nextIndex === null) return

    event.preventDefault()
    this.focusTabAt(tabs, nextIndex)
  }

  getFocusedTabIndex(tabs) {
    const focusedElement = document.activeElement
    return tabs.indexOf(focusedElement)
  }

  getNextTabIndex(key, currentIndex, totalTabs) {
    switch (key) {
      case 'ArrowDown':
      case 'ArrowRight':
        return (currentIndex + 1) % totalTabs
      case 'ArrowUp':
      case 'ArrowLeft':
        return (currentIndex - 1 + totalTabs) % totalTabs
      case 'Home':
        return 0
      case 'End':
        return totalTabs - 1
      default:
        return null
    }
  }

  focusTabAt(tabs, nextIndex) {
    const nextTab = tabs[nextIndex]
    nextTab.focus()
    this.activate(nextTab, nextTab.dataset.aboutText ?? '')
  }

  activate(tab, text) {
    for (const t of this.getTabs()) {
      const isActive = t === tab
      t.setAttribute('aria-selected', isActive ? 'true' : 'false')
      t.setAttribute('tabindex', isActive ? '0' : '-1')
    }
    this.refs.panel.setAttribute('aria-labelledby', tab.id)
    this.swapText(text)
  }

  wrapDisplayInWords() {
    const display = this.refs.display
    const text = display.textContent.trim()
    display.innerHTML = this.buildWordsHTML(text)
  }

  renderHeightGhosts() {
    const panel = this.refs.panel
    const display = this.refs.display
    const fragment = document.createDocumentFragment()
    for (const tab of this.getTabs()) {
      const text = tab.dataset.aboutText ?? ''
      const ghost = document.createElement('span')
      ghost.className = 'about-panel__ghost'
      ghost.setAttribute('aria-hidden', 'true')
      ghost.innerHTML = this.buildWordsHTML(text)
      fragment.appendChild(ghost)
    }
    panel.insertBefore(fragment, display)
  }

  buildWordsHTML(text) {
    return text
      .split(/(\s+)/)
      .map((token) =>
        /^\s+$/.test(token)
          ? token
          : `<span class="about-panel__word">${escapeHTML(token)}</span>`
      )
      .join('')
  }

  swapText(text) {
    const display = this.refs.display
    const requestId = this.nextSwapRequestId()

    if (this.reducedMotionQuery.matches) {
      display.innerHTML = this.buildWordsHTML(text)
      return
    }

    this.activeTimeline?.kill()
    this.animateDisplaySwap(display, text, requestId)
  }

  nextSwapRequestId() {
    this.swapRequestId += 1
    return this.swapRequestId
  }

  async animateDisplaySwap(display, text, requestId) {
    const gsapRuntime = await loadGsapCore()
    if (!this.isCurrentSwapRequest(requestId)) return
    if (!gsapRuntime) {
      display.innerHTML = this.buildWordsHTML(text)
      return
    }

    this.startDisplayTimeline(gsapRuntime, display, text)
  }

  startDisplayTimeline(gsapRuntime, display, text) {
    const outgoing = Array.from(display.querySelectorAll('.about-panel__word'))
    const timeline = this.createDisplayTimeline(gsapRuntime)

    this.animateOutgoingWords(timeline, outgoing)
    this.queueIncomingWords(timeline, gsapRuntime, display, text)
    this.activeTimeline = timeline
  }

  createDisplayTimeline(gsapRuntime) {
    return gsapRuntime.timeline({
      onComplete: () => {
        this.activeTimeline = null
      }
    })
  }

  animateOutgoingWords(timeline, outgoing) {
    if (outgoing.length === 0) return

    timeline.to(outgoing, {
      duration: Transition.outDurationS,
      ease: 'power2.in',
      opacity: 0,
      stagger: Transition.staggerS,
      yPercent: -30
    })
  }

  queueIncomingWords(timeline, gsapRuntime, display, text) {
    timeline.call(() => {
      display.innerHTML = this.buildWordsHTML(text)
      const incoming = display.querySelectorAll('.about-panel__word')
      gsapRuntime.fromTo(
        incoming,
        { opacity: 0, yPercent: 30 },
        {
          duration: Transition.inDurationS,
          ease: 'power3.out',
          opacity: 1,
          stagger: Transition.staggerS,
          yPercent: 0
        }
      )
    })
  }

  isCurrentSwapRequest(requestId) {
    if (!this.isConnected) return false
    if (requestId !== this.swapRequestId) return false

    return true
  }
}

function escapeHTML(str) {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
}

if (!customElements.get('about-panel')) {
  customElements.define('about-panel', AboutPanel)
}
